import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalFooter } from './Modal';
import { Icons } from './Icons';
import { useToast } from './Toast';
import {
  importCSV,
  ExchangeType,
  ParsedTransaction,
  ImportResult,
  groupTransactionsBySymbol,
  getCoinIdFromSymbol
} from '../services/csvImportService';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (transactions: {
    coinId: string;
    symbol: string;
    positions: {
      type: 'buy' | 'sell' | 'mining' | 'staking';
      date: string;
      quantity: number;
      price: number;
      note?: string;
    }[];
  }[]) => Promise<void>;
}

type Step = 'upload' | 'preview' | 'mapping' | 'importing' | 'complete';

interface TokenMapping {
  symbol: string;
  coinId: string | null;
  coinName?: string;
  transactions: ParsedTransaction[];
  selected: boolean;
}

export function CSVImportModal({ isOpen, onClose, onImport }: CSVImportModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [selectedExchange, setSelectedExchange] = useState<ExchangeType>('auto');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [tokenMappings, setTokenMappings] = useState<TokenMapping[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  const resetState = () => {
    setStep('upload');
    setSelectedExchange('auto');
    setImportResult(null);
    setTokenMappings([]);
    setIsLoading(false);
    setImportProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleFileSelect = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error(t('csvImport.errors.invalidFormat', 'Please select a CSV file'));
      return;
    }

    setIsLoading(true);

    try {
      const result = await importCSV(file, selectedExchange);
      setImportResult(result);

      if (result.success && result.transactions.length > 0) {
        // Group by symbol and resolve coin IDs
        const groups = groupTransactionsBySymbol(result.transactions);
        const mappings: TokenMapping[] = [];

        for (const [symbol, transactions] of groups) {
          const coinId = await getCoinIdFromSymbol(symbol);
          mappings.push({
            symbol,
            coinId,
            transactions,
            selected: coinId !== null, // Auto-select if we found a match
          });
        }

        // Sort by number of transactions (descending)
        mappings.sort((a, b) => b.transactions.length - a.transactions.length);
        setTokenMappings(mappings);
        setStep('preview');
      } else {
        setStep('preview');
      }
    } catch (err) {
      console.error('CSV import error:', err);
      setImportResult({
        success: false,
        transactions: [],
        errors: [`Error: ${err}`],
        warnings: [],
        exchange: selectedExchange,
        totalRows: 0,
        importedRows: 0,
        skippedRows: 0,
      });
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [selectedExchange]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const toggleTokenSelection = (symbol: string) => {
    setTokenMappings(prev =>
      prev.map(m =>
        m.symbol === symbol ? { ...m, selected: !m.selected } : m
      )
    );
  };

  const handleImport = async () => {
    const selectedMappings = tokenMappings.filter(m => m.selected && m.coinId);

    if (selectedMappings.length === 0) {
      toast.warning(t('csvImport.errors.noSelection', 'Please select at least one token to import'));
      return;
    }

    setStep('importing');
    setImportProgress(0);

    try {
      const importData = selectedMappings.map(mapping => ({
        coinId: mapping.coinId!,
        symbol: mapping.symbol,
        positions: mapping.transactions.map(tx => ({
          type: tx.type,
          date: tx.date,
          quantity: tx.quantity,
          price: tx.price,
          note: tx.note,
        })),
      }));

      // Simulate progress
      const progressInterval = setInterval(() => {
        setImportProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      await onImport(importData);

      clearInterval(progressInterval);
      setImportProgress(100);
      setStep('complete');
    } catch (err) {
      console.error('Import error:', err);
      toast.error(t('csvImport.errors.importFailed', 'Import failed. Please try again.'));
      setStep('preview');
    }
  };

  const renderUploadStep = () => (
    <div style={{ padding: '20px' }}>
      {/* Exchange Selection */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: 600,
          color: 'var(--text)',
          marginBottom: '8px'
        }}>
          {t('csvImport.selectExchange', 'Select Exchange')}
        </label>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {[
            { id: 'auto', name: t('csvImport.autoDetect', 'Auto-detect'), icon: '🔍' },
            { id: 'binance', name: 'Binance', icon: '🟡' },
            { id: 'coinbase', name: 'Coinbase', icon: '🔵' },
            { id: 'kraken', name: 'Kraken', icon: '🟣' },
            { id: 'cryptocom', name: 'Crypto.com', icon: '🔷' },
            { id: 'kucoin', name: 'KuCoin', icon: '🟢' },
          ].map(exchange => (
            <button
              key={exchange.id}
              onClick={() => setSelectedExchange(exchange.id as ExchangeType)}
              style={{
                padding: '12px 20px',
                borderRadius: '12px',
                border: selectedExchange === exchange.id ? '2px solid var(--accent)' : '2px solid var(--border)',
                background: selectedExchange === exchange.id ? 'var(--accent-light)' : 'var(--card)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: 500,
                color: 'var(--text)',
                transition: 'all 0.2s ease',
              }}
            >
              <span style={{ fontSize: '18px' }}>{exchange.icon}</span>
              {exchange.name}
            </button>
          ))}
        </div>
      </div>

      {/* Drop Zone */}
      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        style={{
          border: `2px dashed ${dragActive ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius: '16px',
          padding: '48px 24px',
          textAlign: 'center',
          cursor: 'pointer',
          background: dragActive ? 'var(--accent-light)' : 'var(--hover)',
          transition: 'all 0.2s ease',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileInputChange}
          hidden
        />

        {isLoading ? (
          <div>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid var(--border)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
              margin: '0 auto 16px',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ color: 'var(--muted)', fontSize: '14px' }}>
              {t('csvImport.processing', 'Processing CSV file...')}
            </p>
          </div>
        ) : (
          <>
            <Icons.Upload size={48} style={{ color: 'var(--muted)', marginBottom: '16px' }} />
            <p style={{ fontSize: '16px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
              {t('csvImport.dropzone.title', 'Drop your CSV file here')}
            </p>
            <p style={{ fontSize: '14px', color: 'var(--muted)' }}>
              {t('csvImport.dropzone.subtitle', 'or click to browse')}
            </p>
          </>
        )}
      </div>

      {/* Help Text */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: 'var(--hover)',
        borderRadius: '12px',
        fontSize: '13px',
        color: 'var(--muted)',
      }}>
        <p style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--text)' }}>
          {t('csvImport.help.title', 'How to export your transaction history:')}
        </p>
        <ul style={{ margin: 0, paddingLeft: '20px', lineHeight: '1.8' }}>
          <li><strong>Binance:</strong> {t('csvImport.help.binance', 'Wallet → Transaction History → Export')}</li>
          <li><strong>Coinbase:</strong> {t('csvImport.help.coinbase', 'Taxes → Generate Report → Transaction History')}</li>
          <li><strong>Kraken:</strong> {t('csvImport.help.kraken', 'History → Export → Ledgers')}</li>
          <li><strong>Crypto.com:</strong> {t('csvImport.help.cryptocom', 'Accounts → Transaction History → Export to CSV')}</li>
          <li><strong>KuCoin:</strong> {t('csvImport.help.kucoin', 'Orders → Spot Trade History → Export')}</li>
        </ul>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    if (!importResult) return null;

    return (
      <div style={{ padding: '20px' }}>
        {/* Summary */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}>
          <div style={{
            padding: '16px',
            background: 'var(--hover)',
            borderRadius: '12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--accent)' }}>
              {importResult.importedRows}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('csvImport.transactionsParsed', 'Transactions parsed')}
            </div>
          </div>
          <div style={{
            padding: '16px',
            background: 'var(--hover)',
            borderRadius: '12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--text)' }}>
              {tokenMappings.length}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('csvImport.tokensFound', 'Tokens found')}
            </div>
          </div>
          <div style={{
            padding: '16px',
            background: 'var(--hover)',
            borderRadius: '12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '24px', fontWeight: 700, color: 'var(--warning)' }}>
              {importResult.skippedRows}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {t('csvImport.rowsSkipped', 'Rows skipped')}
            </div>
          </div>
        </div>

        {/* Errors */}
        {importResult.errors.length > 0 && (
          <div style={{
            padding: '16px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '12px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Icons.AlertTriangle size={18} style={{ color: '#ef4444' }} />
              <span style={{ fontWeight: 600, color: '#ef4444' }}>
                {t('csvImport.errors.title', 'Errors')}
              </span>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#ef4444' }}>
              {importResult.errors.slice(0, 5).map((err, i) => (
                <li key={i}>{err}</li>
              ))}
              {importResult.errors.length > 5 && (
                <li>... {t('csvImport.andMore', 'and')} {importResult.errors.length - 5} {t('csvImport.moreErrors', 'more')}</li>
              )}
            </ul>
          </div>
        )}

        {/* Warnings */}
        {importResult.warnings.length > 0 && (
          <div style={{
            padding: '16px',
            background: 'rgba(245, 158, 11, 0.1)',
            borderRadius: '12px',
            marginBottom: '16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Icons.AlertTriangle size={18} style={{ color: '#f59e0b' }} />
              <span style={{ fontWeight: 600, color: '#f59e0b' }}>
                {t('csvImport.warnings.title', 'Warnings')}
              </span>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#f59e0b' }}>
              {importResult.warnings.map((warn, i) => (
                <li key={i}>{warn}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Token List */}
        {tokenMappings.length > 0 && (
          <div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                {t('csvImport.selectTokens', 'Select tokens to import:')}
              </span>
              <button
                onClick={() => setTokenMappings(prev => prev.map(m => ({ ...m, selected: m.coinId !== null })))}
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'var(--hover)',
                  color: 'var(--accent)',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                {t('csvImport.selectAll', 'Select all valid')}
              </button>
            </div>

            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: '12px',
            }}>
              {tokenMappings.map((mapping, index) => (
                <div
                  key={mapping.symbol}
                  onClick={() => mapping.coinId && toggleTokenSelection(mapping.symbol)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 16px',
                    borderBottom: index < tokenMappings.length - 1 ? '1px solid var(--border)' : 'none',
                    cursor: mapping.coinId ? 'pointer' : 'not-allowed',
                    background: mapping.selected ? 'var(--accent-light)' : 'transparent',
                    opacity: mapping.coinId ? 1 : 0.5,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={mapping.selected}
                    disabled={!mapping.coinId}
                    onChange={() => {}}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text)' }}>
                      {mapping.symbol}
                      {!mapping.coinId && (
                        <span style={{
                          marginLeft: '8px',
                          padding: '2px 6px',
                          background: 'rgba(239, 68, 68, 0.1)',
                          color: '#ef4444',
                          borderRadius: '4px',
                          fontSize: '10px',
                        }}>
                          {t('csvImport.notFound', 'Not found')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                      {mapping.transactions.length} {t('csvImport.transactions', 'transactions')}
                      {' • '}
                      {mapping.transactions.filter(t => t.type === 'buy').length} {t('csvImport.buys', 'buys')}
                      {', '}
                      {mapping.transactions.filter(t => t.type === 'sell').length} {t('csvImport.sells', 'sells')}
                    </div>
                  </div>
                  <Icons.ChevronRight size={18} style={{ color: 'var(--muted)' }} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderImportingStep = () => (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{
        width: '64px',
        height: '64px',
        border: '4px solid var(--border)',
        borderTopColor: 'var(--accent)',
        borderRadius: '50%',
        margin: '0 auto 24px',
        animation: 'spin 1s linear infinite',
      }} />
      <p style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)', marginBottom: '8px' }}>
        {t('csvImport.importing', 'Importing transactions...')}
      </p>
      <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '24px' }}>
        {t('csvImport.pleaseWait', 'Please wait while we import your data')}
      </p>
      <div style={{
        width: '100%',
        height: '8px',
        background: 'var(--border)',
        borderRadius: '4px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${importProgress}%`,
          height: '100%',
          background: 'var(--accent)',
          transition: 'width 0.3s ease',
        }} />
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div style={{ padding: '40px 20px', textAlign: 'center' }}>
      <div style={{
        width: '64px',
        height: '64px',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
        <Icons.Check size={32} style={{ color: 'white' }} />
      </div>
      <p style={{ fontSize: '20px', fontWeight: 700, color: 'var(--text)', marginBottom: '8px' }}>
        {t('csvImport.complete.title', 'Import Complete!')}
      </p>
      <p style={{ fontSize: '14px', color: 'var(--muted)', marginBottom: '24px' }}>
        {t('csvImport.complete.message', 'Your transactions have been imported successfully.')}
      </p>
      <div style={{
        padding: '16px',
        background: 'var(--hover)',
        borderRadius: '12px',
        display: 'inline-block',
      }}>
        <span style={{ fontSize: '14px', color: 'var(--muted)' }}>
          {tokenMappings.filter(m => m.selected).length} {t('csvImport.tokensImported', 'tokens imported')}
          {' • '}
          {tokenMappings.filter(m => m.selected).reduce((sum, m) => sum + m.transactions.length, 0)} {t('csvImport.transactionsImported', 'positions added')}
        </span>
      </div>
    </div>
  );

  const getFooter = () => {
    switch (step) {
      case 'upload':
        return null;
      case 'preview':
        return (
          <ModalFooter>
            <button onClick={resetState} className="btn-ghost">
              {t('csvImport.back', 'Back')}
            </button>
            <button
              onClick={handleImport}
              className="btn-primary"
              disabled={tokenMappings.filter(m => m.selected && m.coinId).length === 0}
            >
              {t('csvImport.import', 'Import')} ({tokenMappings.filter(m => m.selected && m.coinId).length})
            </button>
          </ModalFooter>
        );
      case 'importing':
        return null;
      case 'complete':
        return (
          <ModalFooter>
            <button onClick={handleClose} className="btn-primary">
              {t('csvImport.done', 'Done')}
            </button>
          </ModalFooter>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('csvImport.title', 'Import CSV')}
      size="lg"
      footer={getFooter()}
      closeOnBackdropClick={step !== 'importing'}
      closeOnEscape={step !== 'importing'}
    >
      {step === 'upload' && renderUploadStep()}
      {step === 'preview' && renderPreviewStep()}
      {step === 'importing' && renderImportingStep()}
      {step === 'complete' && renderCompleteStep()}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
}
